import { deviceApi } from "#/api/deviceApi";
import { sitesApi } from "#/api/sitesApi";
import {
  type Device,
  DeviceMobilityType,
  type DeviceRegistrationPayload,
  StatusProcessingType,
} from "#/types/device";
import { ItemStatus } from "#/types/items";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import {
  Button,
  Modal,
  type ModalProps,
  MultiSelect,
  PinInput,
  Select,
  Stepper,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCheck } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import DeviceBasicInfoCard from "./DeviceBasicInfoCard";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  onSuccess: () => void;
}

const DeviceRegistrationDrawer = ({ onSuccess, ...props }: Props) => {
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState<boolean>(false);

  const [code, setCode] = useState<string>();
  const [device, setDevice] = useState<Device>();

  const [active, setActive] = useState<number>(0);
  const emptyForm = {
    name: "",
    site_id: 0,
    mobility_type: null,
    status_processing_type: null,
    handler_name: "",
    fixed_status: "",
    selected_statuses: allowedStatuses,
    scan_threshold_in_seconds: 60,
  };

  const form = useForm<DeviceRegistrationPayload>({
    initialValues: emptyForm,
    validate: {
      name: (value) => (value ? null : t("devices.validation.nameRequired")),
      site_id: (value) => (value ? null : t("devices.validation.siteRequired")),
      mobility_type: (value) => (value ? null : t("devices.validation.siteRequired")),
      status_processing_type: (value) => (value ? null : t("devices.validation.siteRequired")),
      selected_statuses: (value, values) => {
        if (values.status_processing_type === StatusProcessingType.PRODUCT_STATUS_LOOP) {
          return value && value.length > 0 ? null : t("devices.validation.atLeastOneStatus");
        }
        return null;
      },
    },
  });

  const handleSubmit = useCallback(
    async (values: typeof form.values) => {
      if (!device) return;
      setProcessing(true);
      try {
        await deviceApi.bind(device.id, values);
        setProcessing(false);
        setActive(2);
      } catch (error) {
        console.error("🚀 ~ handleSubmit ~ error:", error);
        setProcessing(false);
      }
    },
    [device, form]
  );

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await sitesApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: props.opened,
    staleTime: 1000 * 60,
  });

  const getDevice = useCallback(async () => {
    if (!code) return;
    setProcessing(true);
    try {
      const { data } = await deviceApi.getByRegistrationCode(code);
      setProcessing(false);
      if (data) {
        setDevice(data);
        setActive(1);
      }
    } catch (error) {
      console.log("🚀 ~ onClickNext ~ error:", error);
      setProcessing(false);
    }
  }, [code]);

  const onClickNext = useCallback(() => {
    switch (active) {
      case 0:
        void getDevice();
        break;
      case 1:
        void handleSubmit(form.values);
        break;
      default:
        setActive(0);
        setCode(undefined);
        setDevice(undefined);
        form.reset();
        onSuccess();
        return;
    }
  }, [active, getDevice, handleSubmit, onSuccess, form]);

  return (
    <Modal
      {...props}
      title={t("devices.registration.title")}
      size={active === 0 ? "lg" : "xl"}
      fullScreen={active > 0}
      closeOnClickOutside={false}
      onClose={() => {
        props.onClose();
        setActive(0);
        setCode(undefined);
        form.reset();
      }}
      centered
    >
      <div className="flex flex-col gap-10 mx-auto w-full  items-center justify-center">
        <Stepper
          active={active}
          onStepClick={setActive}
          classNames={{
            root: "w-full",
          }}
          color="black"
        >
          <Stepper.Step label={t("devices.registration.firstStep")} description={t("devices.registration.enterCode")}>
            <div className="w-full flex flex-col gap-4 py-10 items-center">
              <p className="text-gray-500 text-sm">
                {t("devices.registration.enterCodeDescription")}
              </p>
              <PinInput
                placeholder="-"
                size="xl"
                length={6}
                onChange={(e) => {
                  setCode(e);
                }}
              />
            </div>
          </Stepper.Step>
          <Stepper.Step label={t("devices.registration.secondStep")} description={t("devices.registration.enterDetails")}>
            <div className="w-full grid grid-cols-[40%_1fr]  gap-4">
              {device && <DeviceBasicInfoCard device={device} />}
              <form
                className="space-y-8 relative w-full"
                onSubmit={form.onSubmit(handleSubmit)}
              >
                <div className="w-full grid grid-cols-1  gap-4">
                  <TextInput
                    label={t("devices.form.name")}
                    withAsterisk
                    placeholder={t("devices.form.namePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <TextInput
                    label={t("devices.form.handlerName")}
                    placeholder={t("devices.form.handlerNamePlaceholder")}
                    {...form.getInputProps("handler_name")}
                  />

                  <Select
                    label={t("devices.form.site")}
                    withAsterisk
                    placeholder={t("devices.form.sitePlaceholder")}
                    searchable
                    data={sites.map((prod) => {
                      return {
                        label: prod?.name ?? "",
                        value: prod?.id?.toString() ?? "",
                      };
                    })}
                    clearable
                    value={String(form.values.site_id || "")}
                    onChange={(_value) => {
                      form.setFieldValue("site_id", Number(_value));
                    }}
                  />

                  <Select
                    label={t("devices.form.mobilityType")}
                    withAsterisk
                    placeholder={t("devices.form.mobilityTypePlaceholder")}
                    searchable
                    data={Object.values(DeviceMobilityType).map((status) => ({
                      label: sanitize(status),
                      value: status,
                    }))}
                    clearable
                    value={form.values.mobility_type}
                    onChange={(_value) => {
                      form.setFieldValue("mobility_type", _value as DeviceMobilityType);
                    }}
                  />

                  <Select
                    label={t("devices.form.statusProcessingType")}
                    withAsterisk
                    placeholder={t("devices.form.statusProcessingTypePlaceholder")}
                    searchable
                    data={Object.values(StatusProcessingType).map((status) => ({
                      label: sanitize(status),
                      value: status,
                    }))}
                    clearable
                    value={form.values.status_processing_type}
                    onChange={(_value) => {
                      form.setFieldValue("status_processing_type", _value as StatusProcessingType);
                    }}
                  />

                  {form.values.status_processing_type ===
                    StatusProcessingType.PRODUCT_STATUS_LOOP && (
                    <TextInput
                      label={t("devices.form.scanningThreshold")}
                      placeholder={t("devices.form.scanningThresholdPlaceholder")}
                      description={t("devices.form.scanningThresholdDescription")}
                      {...form.getInputProps("scan_threshold_in_seconds")}
                    />
                  )}

                  {form.values.status_processing_type ===
                    StatusProcessingType.PRODUCT_STATUS_LOOP && (
                    <MultiSelect
                      label={t("devices.form.selectedStatuses")}
                      withAsterisk
                      placeholder={t("devices.form.selectedStatusesPlaceholder")}
                      description={t("devices.form.selectedStatusesDescription")}
                      searchable
                      data={allowedStatuses.map((status) => ({
                        label: sanitizeItemStatusLabel(status),
                        value: status,
                      }))}
                      value={form.values.selected_statuses || []}
                      onChange={(value) => form.setFieldValue("selected_statuses", value)}
                      hidePickedOptions={false}
                      styles={{
                        pill: { maxWidth: "fit-content" },
                        input: { minHeight: "auto", height: "auto" },
                        inputField: { flexWrap: "wrap" }
                      }}
                    />
                  )}

                  {form.values.status_processing_type ===
                    StatusProcessingType.SINGLE_FIXED && (
                    <Select
                      label={t("devices.form.processingStatus")}
                      withAsterisk
                      placeholder={t("devices.form.statusProcessingTypePlaceholder")}
                      searchable
                      data={Object.values(ItemStatus).map((status) => ({
                        label: sanitizeItemStatusLabel(status),
                        value: status,
                      }))}
                      clearable
                      value={form.values.fixed_status}
                      onChange={(_value) => {
                        form.setFieldValue("fixed_status", _value as string);
                      }}
                    />
                  )}
                </div>
              </form>
            </div>
          </Stepper.Step>
          <Stepper.Completed>
            <div className="w-full min-h-40 flex flex-col items-center justify-center gap-2">
              <IconCheck color="teal" size={40} />
              <Title order={4}>{t("devices.registration.success")}</Title>
              <Text>
                {t("devices.registration.successDescription")}
              </Text>
            </div>
          </Stepper.Completed>
        </Stepper>

        <div className="w-full grid grid-flow-col gap-4">
          {active != 2 && (
            <Button
              color="black"
              variant="outline"
              type="reset"
              onClick={() => {
                form.reset();
                props.onClose();
              }}
              size="md"
            >
              {t("common.abort")}
            </Button>
          )}

          <PermissionTooltip disabled={!canWrite() && active === 1}>
            <Button
              color="black"
              variant="filled"
              loading={processing}
              size="md"
              disabled={(active === 0 && code?.length != 6) || (active === 1 && !canWrite())}
              onClick={onClickNext}
            >
              {active === 0
                ? t("devices.registration.nextCheckScanner")
                : active === 1
                  ? t("common.save")
                  : t("common.dismiss")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default DeviceRegistrationDrawer;
